'use client';

import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import {
  Loader2,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  SearchIcon,
  Images as ImagesIcon,
  X as XIcon,
} from 'lucide-react';
import Image from 'next/image';
import { cn } from '@/lib/utils';
import { useI18n } from '@/providers/i18n';
import type { SubjectItem } from '@/components/digital-asset-library/subject-library';
import {
  PICKER_DIALOG_CN,
  PICKER_HEADER_CN,
  PICKER_FOOTER_CN,
  PICKER_GRID_VIEWPORT_CN,
  PICKER_GRID_CN,
  PICKER_CARD_CN,
  PICKER_SELECT_TRIGGER_CN,
  PICKER_SELECT_CONTENT_CN,
  PICKER_SELECT_ITEM_CN,
  PICKER_SEGMENT_GROUP_CN,
  pickerSegmentItemCn,
} from './picker-dialog-styles';

type SubjectScope = 'my' | 'public';
type SubjectCategoryFilter = 'all' | 'person' | 'animal' | 'clothing' | 'item' | 'other';
type SubjectSortOrder = 'time_desc' | 'time_asc' | 'hot_desc';

const SUBJECT_CATEGORY_OPTIONS: Array<{ value: 'person' | 'animal' | 'clothing' | 'item' | 'other'; label: string }> = [
  { value: 'person', label: '人物' },
  { value: 'animal', label: '动物' },
  { value: 'clothing', label: '服饰' },
  { value: 'item', label: '物品' },
  { value: 'other', label: '其他' },
];

type SubjectPickerDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (item: SubjectItem) => void;
};

const PAGE_SIZE = 12;

export function SubjectPickerDialog({ open, onOpenChange, onPick }: SubjectPickerDialogProps) {
  const { t } = useI18n();

  const [scope, setScope] = useState<SubjectScope>('my');
  const [categoryFilter, setCategoryFilter] = useState<SubjectCategoryFilter>('all');
  const [sortOrder, setSortOrder] = useState<SubjectSortOrder>('time_desc');
  const [q, setQ] = useState('');
  const [qCommitted, setQCommitted] = useState('');
  const [page, setPage] = useState(1);

  const [items, setItems] = useState<SubjectItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [imgFallbacks, setImgFallbacks] = useState<Set<string>>(new Set());
  const [refreshKey, setRefreshKey] = useState(0);

  // 打开时重置一些状态（保留 scope/filter 给一次会话内的连贯性可能更好，但安全起见每次都从第 1 页开始）
  useEffect(() => {
    if (!open) return;
    setPage(1);
    setImgFallbacks(new Set());
  }, [open]);

  // 任何筛选条件变更时回到第 1 页
  useEffect(() => {
    if (!open) return;
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, categoryFilter, sortOrder, qCommitted]);

  // 拉数据
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams({
          page: String(page),
          perPage: String(PAGE_SIZE),
          scope,
          category: categoryFilter,
          order: sortOrder,
        });
        if (qCommitted.trim()) params.set('q', qCommitted.trim());
        const res = await fetch(`/api/subject-assets?${params.toString()}`);
        const data = await res.json();
        if (cancelled) return;
        if (!res.ok || data?.error) {
          setItems([]);
          setHasMore(false);
          return;
        }
        setItems((data.items || []) as SubjectItem[]);
        setHasMore(Boolean(data.hasMore));
      } catch {
        if (!cancelled) {
          setItems([]);
          setHasMore(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, page, scope, categoryFilter, sortOrder, qCommitted, refreshKey]);

  const handleSelect = (item: SubjectItem) => {
    try {
      onPick(item);
    } finally {
      onOpenChange(false);
    }
  };

  const totalShown = items.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={PICKER_DIALOG_CN}
        onOpenAutoFocus={(e) => e.preventDefault()}
        showCloseButton={false}
      >
        <DialogHeader className={PICKER_HEADER_CN}>
          <div className="flex min-h-[1.5rem] flex-wrap items-center gap-3">
            <DialogTitle className="text-base font-semibold tracking-tight shrink-0">
              {t('Pick from subject library')}
            </DialogTitle>
            <div className={PICKER_SEGMENT_GROUP_CN}>
              <button
                type="button"
                onClick={() => setScope('my')}
                className={pickerSegmentItemCn(scope === 'my')}
              >
                {t('Private')}
              </button>
              <button
                type="button"
                onClick={() => setScope('public')}
                className={pickerSegmentItemCn(scope === 'public')}
              >
                {t('Public')}
              </button>
            </div>
            <div className="ml-auto flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={() => setRefreshKey((k) => k + 1)}
                disabled={loading}
                title={t('Refresh')}
                aria-label={t('Refresh')}
                className={cn(
                  'inline-flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-all',
                  loading
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:bg-sidebar-accent/30 hover:text-foreground active:scale-95',
                )}
              >
                <RefreshCw className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />
              </button>
              <DialogClose
                aria-label={t('Close preview') || 'Close'}
                className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-muted-foreground transition-all hover:bg-sidebar-accent/30 hover:text-foreground active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <XIcon className="h-3.5 w-3.5" />
              </DialogClose>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 mt-2 min-h-[40px]">
            <Select
              value={categoryFilter}
              onValueChange={(v) => setCategoryFilter(v as SubjectCategoryFilter)}
            >
              <SelectTrigger className={cn(PICKER_SELECT_TRIGGER_CN, 'min-w-0 w-[120px]')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className={PICKER_SELECT_CONTENT_CN}>
                <SelectItem value="all" className={PICKER_SELECT_ITEM_CN}>{t('All')}</SelectItem>
                {SUBJECT_CATEGORY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} className={PICKER_SELECT_ITEM_CN}>
                    {t(opt.label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="relative flex-1 min-w-[140px]">
              <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/50" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t('Search subject name')}
                className="pl-8 h-9 text-xs rounded-xl border-none bg-sidebar-accent/50 placeholder:text-muted-foreground/40 shadow-none focus-visible:ring-0"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    setQCommitted(q);
                  }
                }}
              />
            </div>

            <Select value={sortOrder} onValueChange={(v) => setSortOrder(v as SubjectSortOrder)}>
              <SelectTrigger className={cn(PICKER_SELECT_TRIGGER_CN, 'min-w-0 w-[100px]')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className={PICKER_SELECT_CONTENT_CN}>
                <SelectItem value="time_desc" className={PICKER_SELECT_ITEM_CN}>{t('Newest')}</SelectItem>
                <SelectItem value="time_asc" className={PICKER_SELECT_ITEM_CN}>{t('Oldest')}</SelectItem>
                <SelectItem value="hot_desc" className={PICKER_SELECT_ITEM_CN}>{t('Most popular')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </DialogHeader>

        <div className={PICKER_GRID_VIEWPORT_CN}>
          {loading && totalShown === 0 ? (
            <div className="flex h-full w-full items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : totalShown === 0 ? (
            <div className="flex h-full w-full items-center justify-center">
              <p className="text-sm text-muted-foreground text-center px-4">
                {scope === 'public' ? t('No public subjects') : t('暂无主体，点击"新建主体"开始')}
              </p>
            </div>
          ) : (
            <div className={PICKER_GRID_CN}>
              {items.map((item, idx) => {
                const subjectImagesCount = Array.isArray(item.uploadedImage)
                  ? (item.uploadedImage as string[]).filter((u) => typeof u === 'string' && u.trim().length > 0).length
                  : 0;
                const isMulti = item.mode === 'multi' && subjectImagesCount > 1;
                const imageUrl = String(item.image || '').trim();
                const showFallback = !imageUrl || imgFallbacks.has(imageUrl);
                return (
                  <button
                    key={`${item.id || idx}-${page}`}
                    type="button"
                    className={PICKER_CARD_CN}
                    onClick={() => handleSelect(item)}
                    title={item.name}
                  >
                    <div className="relative aspect-square w-full min-h-0 bg-muted">
                      {!showFallback ? (
                        <Image
                          src={imageUrl}
                          alt={item.name || 'subject'}
                          fill
                          unoptimized
                          className="object-cover"
                          onError={() => {
                            if (imageUrl) {
                              setImgFallbacks((prev) => new Set(prev).add(imageUrl));
                            }
                          }}
                        />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center bg-muted/40">
                          <ImagesIcon size={18} className="text-muted-foreground/60" />
                        </div>
                      )}
                      {isMulti ? (
                        <div className="pointer-events-none absolute right-1.5 top-1.5 inline-flex items-center gap-1 rounded-md border border-white/15 bg-black/55 px-1.5 py-0.5 text-[10px] font-bold tracking-wider leading-none text-white shadow-sm ring-1 ring-white/10 backdrop-blur-sm">
                          <ImagesIcon size={10} />
                          <span>×{subjectImagesCount}</span>
                        </div>
                      ) : null}
                    </div>
                    <div className="absolute bottom-0 inset-x-0 px-2 py-1.5 bg-gradient-to-t from-black/60 to-transparent">
                      <p className="text-[11px] font-medium truncate text-white/90">{item.name}</p>
                    </div>
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className={PICKER_FOOTER_CN}>
          <span className="text-xs text-muted-foreground">
            {t('Page_number', { num: page })}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={loading || page <= 1}
              className={cn(
                'flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-all',
                loading || page <= 1
                  ? 'text-muted-foreground/30 cursor-not-allowed'
                  : 'text-muted-foreground hover:text-foreground hover:bg-sidebar-accent/30',
              )}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              {t('Prev')}
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={loading || !hasMore}
              className={cn(
                'flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-all',
                loading || !hasMore
                  ? 'text-muted-foreground/30 cursor-not-allowed'
                  : 'text-muted-foreground hover:text-foreground hover:bg-sidebar-accent/30',
              )}
            >
              {t('Next')}
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
